import React, {useEffect, useRef, useState} from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import HeaderWithBack from '../../components/HeaderWithBack';
import {useNavigate, useParams} from "react-router";
import {EnrichedItemDTO} from "../../dtos/item.dto";
import {toast} from "react-toastify";
import {useRecoilValue} from "recoil";
import {fetchAPIResponseSelector} from "../../store/user.atom";
import {OrderDTO, OrderState} from "../../dtos/order.dto";
import CustomerPrintableOrder from "./CustomerPrintableOrder";
import {STATUS_META} from "../../utils/orderStatus";
import OrderStateSelect from "../../components/OrderStateSelect";

const OrderDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const fetchAPIResponse = useRecoilValue(fetchAPIResponseSelector);

  const [order, setOrder] = useState<OrderDTO | undefined>(undefined);
  const printRef = useRef(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const orderDTOServerResponse = await fetchAPIResponse<OrderDTO>(`/orders/${id}`, {
          method: "GET",
          headers: {
            "Accept": "application/json"
          }
        });

        setOrder(orderDTOServerResponse.data)
      } catch (error) {
        toast.error((error as Error).message);
      }
    };

    fetchOrder();
  }, [id, fetchAPIResponse]);

  const handleStateChange = async (newState: OrderState) => {
    try {
      if(order) {
        setOrder({
          ...order,
          state: newState
        })
      }

      await fetchAPIResponse(`/orders/${order?.id}/status`, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          toState: newState
        })
      });

    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const handlePrint = () => {
    const printContent = document.getElementById('printableOrder');
    const printWindow = window.open('', '', 'height=600,width=800');

    if(printWindow) {
      printWindow.document.write('<html><head><title>Print</title>');
      printWindow.document.write('</head><body >');
      printWindow.document.write(printContent!.innerHTML);
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.print();

      if(order?.state === OrderState.PENDING) {
        handleStateChange(OrderState.PREPARING);
      }
    }
  };


  const m = order ? (STATUS_META[order.state] ?? STATUS_META.PENDING) : STATUS_META.PENDING;

  return (
    <div className='flex flex-col w-full h-full overflow-auto items-center py-10 gap-6'>
      <HeaderWithBack previousPage={'Orders'} currentPage={'Order Details'} previousPageUrl={'/orders'} />
      {order && (
        <>
          <CustomerPrintableOrder ref={printRef} order={order} />
          <div className="w-11/12 grid grid-cols-1 lg:grid-cols-3 gap-6 pb-6">
            {/* The ticket */}
            <div className="lg:col-span-2 zbh-card relative overflow-hidden">
              <span className={`absolute left-0 top-0 h-full w-1.5 ${m.spine}`} />
              <div className="flex items-start justify-between gap-4 p-6 pl-7">
                <div>
                  <p className="zbh-eyebrow">Order</p>
                  <p className="zbh-figure text-xl font-bold text-ink">#{order.id.slice(-8)}</p>
                  <p className="zbh-figure mt-1 text-xs text-categoriesTextColor">
                    {new Date(order.order_date).toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', month: 'long', day: '2-digit', year: 'numeric' })}
                  </p>
                </div>
                <OrderStateSelect
                    value={order.state}
                    onChange={(s) => handleStateChange(s)}
                />
              </div>

              <div className="zbh-tear mx-6" />

              {/* Line items */}
              <div className="p-6 space-y-5">
                {order.items.map((item) => (
                    <div key={item.id} className="flex gap-4">
                      <img src={item.image} alt={item.name} className="h-14 w-14 shrink-0 rounded-lg object-cover border border-hair" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-3">
                          <p className="text-sm font-semibold text-ink">
                            <span className="zbh-figure text-categoriesTextColor">{item.quantity}×</span> {item.name}
                          </p>
                          <span className="zbh-figure text-sm font-bold text-ink whitespace-nowrap">${item.price}</span>
                        </div>
                        {item.contents.length > 0 && (
                            <ul className="mt-1.5 space-y-0.5">
                              {item.contents.map((content, index) => (
                                  <li key={index} className="text-xs text-categoriesTextColor">{content}</li>
                              ))}
                            </ul>
                        )}
                        {item.note && (
                            <p className="mt-1.5 inline-block rounded-md bg-accent-soft px-2 py-0.5 text-xs text-accent-ink">Note: {item.note}</p>
                        )}
                      </div>
                    </div>
                ))}
              </div>

              <div className="zbh-tear mx-6" />

              {/* Totals */}
              <div className="p-6 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-categoriesTextColor">Subtotal</span>
                  <span className="zbh-figure text-ink">${order.subtotal}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-categoriesTextColor">Delivery</span>
                  <span className="zbh-figure text-ink">${order.delivery}</span>
                </div>
                {typeof order.discount === 'number' && order.discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-categoriesTextColor">Discount</span>
                    <span className="zbh-figure text-state-delivered">−${order.discount}</span>
                  </div>
                )}
                <div className="zbh-tear my-2" />
                <div className="flex items-baseline justify-between">
                  <span className="font-display text-base font-semibold text-ink">Total</span>
                  <span className="zbh-figure text-2xl font-bold text-ink">${order.total}</span>
                </div>
              </div>
            </div>

            {/* Side panel: customer, address, actions */}
            <div className="space-y-6">
              <div className="zbh-card p-6">
                <p className="zbh-eyebrow">Customer</p>
                <div className="mt-3 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-soft text-accent-ink text-sm font-bold">
                    {order.user?.name?.trim()?.charAt(0)?.toUpperCase() ?? '?'}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-ink">{order.user?.name}</p>
                    <p className="zbh-figure text-xs text-categoriesTextColor">{order.user?.phone_number}</p>
                  </div>
                </div>
                {order.user?.email && (
                  <p className="mt-3 truncate text-xs text-categoriesTextColor">{order.user.email}</p>
                )}
              </div>

              <div className="zbh-card p-6">
                <p className="zbh-eyebrow">{order.address ? 'Delivery address' : 'Fulfilment'}</p>
                {order.address ? (
                  <div className="mt-3 space-y-1 text-sm text-ink">
                    <p className="font-medium">{order.address.building}</p>
                    <p className="text-categoriesTextColor">{order.address.street}</p>
                    <p className="text-categoriesTextColor">Apartment {order.address.apartment}</p>
                    {order.address.additional_information && (
                      <p className="mt-2 rounded-md bg-canvas px-2 py-1 text-xs text-categoriesTextColor">{order.address.additional_information}</p>
                    )}
                  </div>
                ) : (
                  <p className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-ink">
                    <span className="h-2 w-2 rounded-full bg-accent" /> Pickup
                  </p>
                )}
              </div>

              <button className="zbh-btn-primary w-full" onClick={handlePrint}>
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9V3h12v6" /><path d="M6 18H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2" />
                  <rect x="6" y="14" width="12" height="7" rx="1" />
                </svg>
                Print ticket
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OrderDetails;
